import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { formatDistanceToNow, format, isToday, isYesterday } from "date-fns";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: Date | string): string {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  
  if (isToday(dateObj)) {
    return `Today, ${format(dateObj, "h:mm a")}`;
  } else if (isYesterday(dateObj)) {
    return `Yesterday, ${format(dateObj, "h:mm a")}`;
  } else if (dateObj.getFullYear() === new Date().getFullYear()) {
    return format(dateObj, "MMM d");
  } else {
    return format(dateObj, "MMM d, yyyy");
  }
}

export function formatRelativeDate(date: Date | string): string {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return formatDistanceToNow(dateObj, { addSuffix: true });
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "…";
}

export function generateAvatarPlaceholder(name?: string): string {
  if (!name || typeof name !== 'string') {
    return 'U';
  }
  
  return name
    .split(" ")
    .map((n) => n?.[0] || '')
    .join("")
    .toUpperCase()
    .substring(0, 2) || 'U';
}

export function getMediaTypeIcon(mediaType: string): string {
  switch (mediaType) {
    case "image":
      return "image";
    case "video":
      return "videocam";
    case "audio":
      return "mic";
    case "pdf":
      return "description";
    default:
      return "insert_drive_file";
  }
}

export function getEventTypeIcon(eventType: string): string {
  switch (eventType) {
    case "game":
      return "sports_basketball";
    case "practice":
      return "fitness_center";
    case "college_visit":
      return "school";
    case "tournament":
      return "emoji_events";
    case "training":
      return "sports";
    default:
      return "event";
  }
}

export function getMilestoneTypeIcon(type: string): string {
  switch (type) {
    case "achievement":
      return "military_tech";
    case "recruiting":
      return "school";
    case "training":
      return "fitness_center";
    default:
      return "flag";
  }
}

export function getCategoryColor(category: string): string {
  switch (category?.toLowerCase()) {
    case "game reflection":
      return "bg-primary/10 text-primary";
    case "training":
      return "bg-secondary/10 text-secondary";
    case "leadership":
      return "bg-accent/10 text-accent";
    case "mental preparation":
      return "bg-purple-500/10 text-purple-500";
    case "milestone":
      return "bg-amber-500/10 text-amber-500";
    default:
      return "bg-neutral-500/10 text-neutral-500";
  }
}

export function hexToRgb(hex: string) {
  // Remove the # character if it exists
  hex = hex.replace('#', '');
  
  // Parse the hexadecimal value to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return { r, g, b };
}

export function generateRgbOpacity(hex: string, opacity: number) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export function dataURLtoFile(dataurl: string, filename: string) {
  if (!dataurl || typeof dataurl !== 'string') {
    console.warn('dataURLtoFile: Invalid data URL provided', dataurl);
    return new File([''], filename, { type: 'application/octet-stream' });
  }
  
  const arr = dataurl.split(',');
  if (arr.length < 2 || !arr[0] || !arr[1]) {
    console.warn('dataURLtoFile: Invalid data URL format', dataurl);
    return new File([''], filename, { type: 'application/octet-stream' });
  }
  
  let mime = 'application/octet-stream';
  try {
    // Ensure arr[0] exists and is a string before calling match
    if (arr[0] && typeof arr[0] === 'string') {
      const mimeMatch = arr[0].match(/:(.*?);/);
      mime = mimeMatch?.[1] || 'application/octet-stream';
    } else {
      console.warn('dataURLtoFile: arr[0] is not a valid string:', arr[0]);
    }
  } catch (error) {
    console.warn('dataURLtoFile: Failed to parse MIME type', error, 'arr[0]:', arr[0]);
  }
  
  try {
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    
    return new File([u8arr], filename, { type: mime });
  } catch (error) {
    console.warn('dataURLtoFile: Failed to decode data URL', error);
    return new File([''], filename, { type: mime });
  }
}
